//
// Display Home Media Panel
// --------------------------------------------------
// Handles: tab toggle switching, thumb gallery swiper, virtual tour iframe, comparison slider

import Swiper from 'swiper/bundle';

window.addEventListener('load', () => {
    const mediaPanels = document.querySelectorAll('[data-display-home-media]');

    mediaPanels.forEach(mediaPanel => {
        initTabSwitcher(mediaPanel);
        initGallerySwiper(mediaPanel);
        initVirtualTour(mediaPanel);
        initYoutube(mediaPanel);
        initComparisonSlider(mediaPanel);
    });
});

// ========================================
// Tab Switcher
// ========================================
function initTabSwitcher(container) {
    const toggleButtons = container.querySelectorAll('[data-media-tab]');
    const panels = container.querySelectorAll('[data-media-panel]');

    if (toggleButtons.length <= 1) return;

    // Pointer hint animation
    initPointerHint(container, toggleButtons);

    toggleButtons.forEach(button => {
        button.addEventListener('click', () => {
            const targetTab = button.dataset.mediaTab;

            // Update button states
            toggleButtons.forEach(btn => {
                btn.dataset.active = 'false';
                btn.setAttribute('aria-selected', 'false');
            });
            button.dataset.active = 'true';
            button.setAttribute('aria-selected', 'true');

            // Fade panels
            panels.forEach(panel => {
                const isTarget = panel.dataset.mediaPanel === targetTab;
                panel.dataset.visible = isTarget ? 'true' : 'false';
            });

            // Dispatch event so child components can recalculate
            container.dispatchEvent(new CustomEvent('media-tab-change', { detail: { tab: targetTab } }));
        });
    });
}

// ========================================
// Gallery Swiper with Thumbs
// ========================================
function initGallerySwiper(container) {
    const galleryContainer = container.querySelector('[data-swiper-type="display-home-gallery"]');
    const thumbsContainer = container.querySelector('[data-swiper-type="display-home-gallery-thumbs"]');

    if (!galleryContainer) return;

    const currentCounter = container.querySelector('[data-gallery-current]');
    const prevEl = container.querySelector('[data-swiper-go-prev="display-home-gallery-0"]');
    const nextEl = container.querySelector('[data-swiper-go-next="display-home-gallery-0"]');

    // Initialize thumbs swiper first
    let thumbsSwiper = null;
    if (thumbsContainer) {
        thumbsSwiper = new Swiper(thumbsContainer, {
            spaceBetween: 8,
            slidesPerView: 'auto',
            freeMode: true,
            watchSlidesProgress: true,
            centeredSlides: false,
        });
    }

    // Initialize main gallery swiper
    const gallerySwiper = new Swiper(galleryContainer, {
        effect: 'fade',
        fadeEffect: { crossFade: true },
        speed: 600,
        loop: false,
        allowTouchMove: true,
        resistanceRatio: 0,
        thumbs: thumbsSwiper ? { swiper: thumbsSwiper } : undefined,
        navigation: {
            nextEl: nextEl,
            prevEl: prevEl,
        },
    });

    // Update counter on slide change
    if (currentCounter) {
        gallerySwiper.on('slideChange', () => {
            currentCounter.textContent = gallerySwiper.activeIndex + 1;
        });
    }

    // Update navigation button states
    function updateNavState() {
        if (prevEl) {
            prevEl.classList.toggle('opacity-40', gallerySwiper.isBeginning);
            prevEl.classList.toggle('pointer-events-none', gallerySwiper.isBeginning);
        }
        if (nextEl) {
            nextEl.classList.toggle('opacity-40', gallerySwiper.isEnd);
            nextEl.classList.toggle('pointer-events-none', gallerySwiper.isEnd);
        }
    }

    gallerySwiper.on('slideChange reachBeginning reachEnd', updateNavState);
    updateNavState();
}

// ========================================
// Virtual Tour Iframe
// ========================================
function initVirtualTour(container) {
    const iframe = container.querySelector('[data-tour-iframe]');
    const loadingOverlay = container.querySelector('[data-tour-loading]');

    if (!iframe) return;

    const src = iframe.dataset.src;
    if (!src) return;

    let tourLoaded = false;
    const firstTab = container.dataset.firstTab;

    function loadTour() {
        if (tourLoaded) return;
        tourLoaded = true;

        iframe.src = src;

        // When iframe loads, fade it in and hide the loading overlay
        iframe.addEventListener('load', () => {
            // Fade in the iframe
            iframe.style.opacity = '1';

            // Hide loading overlay
            if (loadingOverlay) {
                loadingOverlay.setAttribute('data-tour-loaded', 'true');
            }
        });

        // Fallback: if iframe takes too long, still show it after 5s
        setTimeout(() => {
            iframe.style.opacity = '1';
            if (loadingOverlay) {
                loadingOverlay.setAttribute('data-tour-loaded', 'true');
            }
        }, 5000);
    }

    // If virtual tour is the first/only active tab, load immediately
    if (firstTab === 'virtual-tour') {
        loadTour();
    }

    // Load tour when the virtual tour tab is switched to
    container.addEventListener('media-tab-change', (e) => {
        if (e.detail.tab === 'virtual-tour') {
            loadTour();
        }
    });
}

// ========================================
// YouTube Iframe
// ========================================
function initYoutube(container) {
    const iframe = container.querySelector('[data-youtube-iframe]');
    const loadingOverlay = container.querySelector('[data-youtube-loading]');

    if (!iframe) return;

    const src = iframe.dataset.src;
    if (!src) return;

    let youtubeLoaded = false;
    const firstTab = container.dataset.firstTab;

    function loadYoutube() {
        if (youtubeLoaded) return;
        youtubeLoaded = true;

        iframe.src = src;

        iframe.addEventListener('load', () => {
            iframe.style.opacity = '1';

            if (loadingOverlay) {
                loadingOverlay.setAttribute('data-tour-loaded', 'true');
            }
        });

        // Fallback: if iframe takes too long, still show it after 5s
        setTimeout(() => {
            iframe.style.opacity = '1';
            if (loadingOverlay) {
                loadingOverlay.setAttribute('data-tour-loaded', 'true');
            }
        }, 5000);
    }

    // If youtube is the first/only active tab, load immediately
    if (firstTab === 'youtube') {
        loadYoutube();
    }

    // Load youtube when the tab is switched to
    container.addEventListener('media-tab-change', (e) => {
        if (e.detail.tab === 'youtube') {
            loadYoutube();
        }
    });
}

// ========================================
// Comparison Slider
// ========================================
function initComparisonSlider(container) {
    const slider = container.querySelector('[data-comparison-slider]');
    if (!slider) return;

    const overlay = slider.querySelector('[data-comparison-overlay]');
    const overlayInner = slider.querySelector('[data-comparison-overlay-inner]');
    const handle = slider.querySelector('[data-comparison-handle]');

    if (!overlay || !handle) return;

    let isDragging = false;
    let currentPct = 0.5;
    let hasAnimatedIn = false;

    function getPosition(e) {
        const rect = slider.getBoundingClientRect();
        let x;
        if (e.touches) {
            x = e.touches[0].clientX - rect.left;
        } else {
            x = e.clientX - rect.left;
        }
        // Clamp between 2% and 98%
        return Math.max(0.02, Math.min(0.98, x / rect.width));
    }

    function updatePosition(pct) {
        currentPct = pct;
        const percent = pct * 100;
        overlay.style.width = `${percent}%`;
        handle.style.left = `${percent}%`;
        syncInnerWidth();
    }

    function syncInnerWidth() {
        if (overlayInner && slider.offsetWidth > 0) {
            overlayInner.style.width = `${slider.offsetWidth}px`;
        }
    }

    // Entrance animation: slide handle from left to center
    function playEntranceAnimation() {
        if (hasAnimatedIn) return;
        hasAnimatedIn = true;

        // Start from 20%
        updatePosition(0.2);

        // Animate to 50% using CSS-based timing
        let start = null;
        const duration = 800;
        const startPct = 0.2;
        const endPct = 0.5;

        function animate(timestamp) {
            if (!start) start = timestamp;
            const elapsed = timestamp - start;
            const progress = Math.min(elapsed / duration, 1);

            // Ease out with slight overshoot (spring feel)
            const eased = 1 - Math.pow(1 - progress, 3);
            const pct = startPct + (endPct - startPct) * eased;

            updatePosition(pct);

            if (progress < 1) {
                requestAnimationFrame(animate);
            }
        }

        requestAnimationFrame(animate);
    }

    function onStart(e) {
        e.preventDefault();
        isDragging = true;
        slider.style.cursor = 'ew-resize';
    }

    function onMove(e) {
        if (!isDragging) return;
        e.preventDefault();
        const pct = getPosition(e);
        requestAnimationFrame(() => updatePosition(pct));
    }

    function onEnd() {
        isDragging = false;
        slider.style.cursor = '';
    }

    // Mouse events
    handle.addEventListener('mousedown', onStart);
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onEnd);

    // Touch events
    handle.addEventListener('touchstart', onStart, { passive: false });
    document.addEventListener('touchmove', onMove, { passive: false });
    document.addEventListener('touchend', onEnd);

    // Click anywhere on the slider to reposition
    slider.addEventListener('click', (e) => {
        if (e.target.closest('[data-comparison-handle]')) return;
        const pct = getPosition(e);
        updatePosition(pct);
    });

    // Handle resize to keep inner image width correct
    const resizeObserver = new ResizeObserver(() => {
        syncInnerWidth();
    });
    resizeObserver.observe(slider);

    // Recalculate when this tab becomes visible
    container.addEventListener('media-tab-change', (e) => {
        if (e.detail.tab === 'comparison') {
            // Small delay to allow CSS transition to make panel visible
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    if (!hasAnimatedIn) {
                        playEntranceAnimation();
                    } else {
                        updatePosition(currentPct);
                    }
                });
            });
        }
    });

    // Initial setup - only if slider is visible (it's the first tab)
    const firstTab = container.dataset.firstTab;
    if (firstTab === 'comparison' && slider.offsetWidth > 0) {
        playEntranceAnimation();
    } else if (slider.offsetWidth > 0) {
        updatePosition(0.5);
    }
}

// ========================================
// Pointer Hint Animation
// ========================================
// Animated pointer that travels across toggle buttons in a U-shaped arc,
// tapping each inactive button with a ripple pulse. Dismissed on first interaction.
function initPointerHint(container, toggleButtons) {
    const hint = container.querySelector('[data-media-pointer-hint]');
    if (!hint) return;

    const ripple = hint.querySelector('[data-pointer-ripple]');
    const bar = container.querySelector('[data-media-toggle-bar]');
    if (!bar) return;

    let dismissed = false;
    let animationId = null;

    // Get inactive button positions relative to the bar
    function getTargets() {
        const barRect = bar.getBoundingClientRect();
        const targets = [];
        toggleButtons.forEach(btn => {
            if (btn.dataset.active === 'true') return;
            const btnRect = btn.getBoundingClientRect();
            targets.push({
                // Centre the 22px-wide icon on the button
                x: btnRect.left - barRect.left + btnRect.width / 2 - 11,
                // 3/4 down the button
                y: btnRect.top - barRect.top + btnRect.height * 0.75,
            });
        });
        return targets;
    }

    // Animate pointer position with easing
    function animateTo(x1, y1, x2, y2, duration, onDone) {
        const dipY = Math.max(y1, y2) + 24; // U-shape dip below both points
        let start = null;

        function step(ts) {
            if (dismissed) return;
            if (!start) start = ts;
            const t = Math.min((ts - start) / duration, 1);

            // Cubic ease-in-out
            const ease = t < 0.5
                ? 4 * t * t * t
                : 1 - Math.pow(-2 * t + 2, 3) / 2;

            // U-shaped path: quadratic bezier with control point at dipY
            const invT = 1 - ease;
            const currentX = invT * x1 + ease * x2;
            const currentY = invT * invT * y1 + 2 * invT * ease * dipY + ease * ease * y2;

            hint.style.left = `${currentX}px`;
            hint.style.top = `${currentY}px`;

            if (t < 1) {
                animationId = requestAnimationFrame(step);
            } else {
                onDone && onDone();
            }
        }

        animationId = requestAnimationFrame(step);
    }

    // Trigger a tap: scale up + ripple pulse
    function playTap(onDone) {
        hint.setAttribute('data-tapping', '');
        ripple.removeAttribute('data-pulse');

        // Force reflow so animation restarts
        void ripple.offsetWidth;
        ripple.setAttribute('data-pulse', '');

        setTimeout(() => {
            hint.removeAttribute('data-tapping');
            setTimeout(() => onDone && onDone(), 300);
        }, 200);
    }

    // Run the full animation loop
    function runCycle() {
        if (dismissed) return;

        const targets = getTargets();
        if (targets.length === 0) { dismiss(); return; }

        let idx = 0;

        // Position at first target instantly and fade in
        hint.style.left = `${targets[0].x}px`;
        hint.style.top = `${targets[0].y}px`;
        hint.setAttribute('data-visible', '');

        function visitNext() {
            if (dismissed) return;

            // Tap at current position
            playTap(() => {
                if (dismissed) return;
                idx++;

                if (idx < targets.length) {
                    // Animate to next target via U-shape
                    const prev = targets[idx - 1];
                    const next = targets[idx];
                    animateTo(prev.x, prev.y, next.x, next.y, 600, visitNext);
                } else {
                    // Cycle complete — fade out, then wait 3s before restarting
                    hint.removeAttribute('data-visible');
                    setTimeout(() => {
                        if (dismissed) return;
                        runCycle();
                    }, 2500);
                }
            });
        }

        // Start after a brief pause
        setTimeout(visitNext, 400);
    }

    function dismiss() {
        if (dismissed) return;
        dismissed = true;
        if (animationId) cancelAnimationFrame(animationId);
        hint.setAttribute('data-dismissed', '');
        setTimeout(() => hint.remove(), 400);
    }

    // Dismiss on any tab click
    toggleButtons.forEach(btn => {
        btn.addEventListener('click', dismiss, { once: true });
    });

    // Start the animation after page settles
    setTimeout(runCycle, 1500);
}
